import React, { useState, useEffect, useCallback } from 'react';
import { getPortalStatus } from '../Services/services';
import { useBranding } from '../Context/BrandingContext';

function Offline() {
  const [lastChecked, setLastChecked] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const { branding } = useBranding();

  const checkPortalStatus = useCallback(async () => {
    setIsChecking(true);
    try {
      const response = await getPortalStatus();
      const data = response.data;

      if (data.online === true) {
        // Portal is back online, redirect to main page
        window.location.replace('/');
        return;
      }
    } catch (error) {
      console.log('Portal status check failed:', error);
    } finally {
      setLastChecked(new Date());
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    // Check immediately on mount
    checkPortalStatus();

    // Auto-poll every 30 seconds
    const interval = setInterval(checkPortalStatus, 30000);
    return () => clearInterval(interval);
  }, [checkPortalStatus]);

  // Dynamic styles based on branding
  const dynamicStyles = {
    subtitle: {
      ...styles.subtitle,
      color: branding.primaryColor,
    },
    divider: {
      ...styles.divider,
      background: `linear-gradient(90deg, transparent, ${branding.primaryColor}, transparent)`,
    },
    featureIcon: {
      ...styles.featureIcon,
      color: branding.primaryColor,
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Aviation Icon */}
        <div style={styles.iconContainer}>
          <svg
            style={styles.icon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 16V14L13 9V3.5C13 2.67 12.33 2 11.5 2C10.67 2 10 2.67 10 3.5V9L2 14V16L10 13.5V19L8 20.5V22L11.5 21L15 22V20.5L13 19V13.5L21 16Z"
              fill={branding.primaryColor}
            />
          </svg>
        </div>

        <h1 style={styles.title}>Coming Soon</h1>
        <h2 style={dynamicStyles.subtitle}>{branding.companyName} Marketplace</h2>

        <div style={dynamicStyles.divider} />

        <p style={styles.description}>
          Welcome to the future home of the {branding.companyName} Marketplace - your gateway to streamlined 
          aviation parts procurement and MRO services.
        </p>

        <div style={styles.featuresContainer}>
          <h3 style={styles.featuresTitle}>What's Coming</h3>
          <div style={styles.featuresList}>
            <div style={styles.featureItem}>
              <div style={dynamicStyles.featureIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              </div>
              <div style={styles.featureText}>
                <strong>Browse Real-Time Inventory</strong>
                <span>Access live stock levels across our network of certified suppliers</span>
              </div>
            </div>

            <div style={styles.featureItem}>
              <div style={dynamicStyles.featureIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
              </div>
              <div style={styles.featureText}>
                <strong>Generate RFQs</strong>
                <span>Request quotes from multiple vendors with a single submission</span>
              </div>
            </div>

            <div style={styles.featureItem}>
              <div style={dynamicStyles.featureIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <div style={styles.featureText}>
                <strong>Approve Quotes</strong>
                <span>Review and approve vendor quotes through a streamlined workflow</span>
              </div>
            </div>

            <div style={styles.featureItem}>
              <div style={dynamicStyles.featureIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
              </div>
              <div style={styles.featureText}>
                <strong>Make Purchases</strong>
                <span>Complete transactions securely with trusted aviation suppliers</span>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <div style={styles.statusContainer}>
            <div style={styles.statusDot} />
            <span style={styles.statusText}>
              {isChecking ? 'Checking status...' : 'Checking for updates'}
            </span>
          </div>
          {lastChecked && (
            <span style={styles.lastChecked}>
              Last checked: {lastChecked.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div style={styles.brandFooter}>
        <span style={styles.brandText}>Powered by {branding.companyName}</span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f0f4f8',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  card: {
    backgroundColor: 'white',
    padding: '48px',
    borderRadius: '12px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    width: '100%',
    maxWidth: '600px',
    textAlign: 'center',
  },
  iconContainer: {
    marginBottom: '24px',
  },
  icon: {
    width: '72px',
    height: '72px',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a1a2e',
    letterSpacing: '-0.5px',
  },
  subtitle: {
    margin: '0 0 24px 0',
    fontSize: '20px',
    fontWeight: '500',
    color: '#0066cc',
  },
  divider: {
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #0066cc, transparent)',
    margin: '0 auto 24px auto',
    width: '60%',
  },
  description: {
    margin: '0 0 32px 0',
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#4a5568',
  },
  featuresContainer: {
    textAlign: 'left',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '32px',
  },
  featuresTitle: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  featureIcon: {
    color: '#0066cc',
    flexShrink: 0,
    marginTop: '2px',
  },
  featureText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontSize: '14px',
    color: '#4a5568',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#48bb78',
    animation: 'pulse 2s infinite',
  },
  statusText: {
    fontSize: '13px',
    color: '#718096',
  },
  lastChecked: {
    fontSize: '12px',
    color: '#a0aec0',
  },
  brandFooter: {
    marginTop: '24px',
  },
  brandText: {
    fontSize: '13px',
    color: '#a0aec0',
  },
};

// Add pulse animation via style tag
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;
document.head.appendChild(styleSheet);

export default Offline;
